import { FC, useEffect, useMemo, useState } from 'react';
import { DreamPrice, fetchDreamPrice } from '../../api/dreamPrices';
import PageHeaderWithBack from '../../components/PageHeaderWithBack';

interface DreamPricesDetailPanelProps {
  id: string;
  onBack: () => void;
}

const formatBoolean = (value: boolean) => (value ? 'Yes' : 'No');

const formatGroupLabel = (value: unknown, index: number) => {
  if (typeof value === 'string') {
    return value;
  }

  if (value && typeof value === 'object') {
    const maybeName = (value as { name?: unknown }).name;
    if (maybeName && typeof maybeName === 'string') {
      return maybeName;
    }

    const maybeLabel = (value as { label?: unknown }).label;
    if (maybeLabel && typeof maybeLabel === 'string') {
      return maybeLabel;
    }

    const maybeId = (value as { id?: unknown }).id;
    if (maybeId !== undefined && maybeId !== null) {
      return `Group ${maybeId}`;
    }
  }

  return `Group ${index + 1}`;
};

const formatDate = (value: string | null, withTime = false) => {
  if (!value) {
    return '—';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '—';
  }

  return withTime ? parsed.toLocaleString() : parsed.toLocaleDateString();
};

const DreamPricesDetailPanel: FC<DreamPricesDetailPanelProps> = ({ id, onBack }) => {
  const [data, setData] = useState<DreamPrice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    fetchDreamPrice(id)
      .then((response) => {
        if (isMounted) {
          setData(response);
        }
      })
      .catch((err) => {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load dream price.');
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [id]);

  const memberGroups = useMemo(() => data?.memberGroups ?? [], [data?.memberGroups]);
  const employeeGroups = useMemo(() => data?.employeeGroups ?? [], [data?.employeeGroups]);
  const products = useMemo(() => data?.products ?? [], [data?.products]);
  const locations = useMemo(() => data?.locations ?? [], [data?.locations]);

  if (loading) {
    return (
      <div className="rounded-2xl bg-white px-6 py-5 text-sm text-slate-500 shadow-inner">
        Loading dream price...
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-2xl bg-white px-6 py-5 text-sm text-danger shadow-inner">
        {error ?? 'Dream price not found.'}
        <div className="mt-4">
          <button
            type="button"
            className="rounded-xl bg-primary px-4 py-2 text-sm text-white shadow"
            onClick={onBack}
          >
            Back to dream prices
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white px-6 py-5 shadow-inner">
      
      <PageHeaderWithBack
        title={`Dream Price #${data.erpId}`}
        description={<p className="mt-1 text-sm text-slate-500">Status: {data.status ?? '—'}</p>}
        onBack={onBack}
        backLabel="Back to dream prices"
      />

      <div className="mt-6 grid gap-6 xl:grid-cols-[320px_1fr]">
        <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-600">
          <h3 className="text-base font-semibold text-slate-700">Overview</h3>
          <dl className="mt-4 space-y-3 text-sm text-slate-700">
            <div className="mb-1">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">ERP ID</dt>
              <dd>{data.erpId}</dd>
            </div>
            <div className="mb-1">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Start Date</dt>
              <dd>{formatDate(data.startDate)}</dd>
            </div>
            <div className="mb-1">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">End Date</dt>
              <dd>{formatDate(data.endDate)}</dd>
            </div>
            <div className="mb-1">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Walk-in Members</dt>
              <dd>{formatBoolean(data.allowWalkInMember)}</dd>
            </div>
            <div className="mb-1">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Registered Members</dt>
              <dd>{formatBoolean(data.allowRegisteredMember)}</dd>
            </div>
            <div className="mb-1">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Employees</dt>
              <dd>{formatBoolean(data.allowEmployee)}</dd>
            </div>
            <div className="mb-1">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Last Updated</dt>
              <dd>{formatDate(data.updatedAt, true)}</dd>
            </div>
          </dl>
        </div>

        <div className="space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-600">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-700">Products</h3>
              <span className="text-xs text-slate-400 ml-2">({products.length} total)</span>
            </div>
            {products.length ? (
              <ul className="space-y-3">
                {products.map((product, index) => (
                  <li key={product.id ?? index} className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                    <div className="text-sm font-semibold text-slate-700">
                      {product.name ?? `Product ${product.productId ?? product.id}`}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      Price: {product.price !== undefined && product.price !== null ? product.price : '—'}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-slate-400">No products linked to this dream price.</p>
            )}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-600">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-700">Locations</h3>
              <span className="text-xs text-slate-400 ml-2">({locations.length} total)</span>
            </div>
            {locations.length ? (
              <ul className="space-y-2">
                {locations.map((location, index) => (
                  <li
                    key={location.id ?? index}
                    className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600"
                  >
                    {location.name ?? `Location ${location.id}`}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-slate-400">No locations linked to this dream price.</p>
            )}
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-600">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-base font-semibold text-slate-700">Member Groups</h3>
                <span className="text-xs text-slate-400 ml-2 ">({memberGroups.length} total)</span>
              </div>
              {memberGroups.length ? (
                <ul className="space-y-2">
                  {memberGroups.map((group, index) => (
                    <li
                      key={index}
                      className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600"
                    >
                      {formatGroupLabel(group, index)}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-slate-400">No member groups configured.</p>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-600">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-base font-semibold text-slate-700">Employee Groups</h3>
                <span className="text-xs text-slate-400 ml-2">({employeeGroups.length} total)</span>
              </div>
              {employeeGroups.length ? (
                <ul className="space-y-2">
                  {employeeGroups.map((group, index) => (
                    <li
                      key={index}
                      className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600"
                    >
                      {formatGroupLabel(group, index)}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-slate-400">No employee groups configured.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DreamPricesDetailPanel;
